// SPDX-License-Identifier: GPL-3.0-or-later
// Animation names for decorations and for the two text channels.
// Grammar: `name` or `name:arg`. The arg is the text after the first colon.
// The rules write these strings. The characters and the client read them.
// This file has no imports and no DOM.

/** A parsed animation string. */
export interface ParsedAnimation {
  readonly name: string;
  /** Text after the first colon. Empty when there is none. */
  readonly arg: string;
}

/** Split `name:arg` at the first colon. */
export function parseAnimation(animation: string): ParsedAnimation {
  const i = animation.indexOf(":");
  if (i < 0) return { name: animation, arg: "" };
  return { name: animation.slice(0, i), arg: animation.slice(i + 1) };
}

/** Read a finite number from an arg. Return `fallback` when the arg is not a number. */
export function numberArg(arg: string, fallback: number): number {
  if (arg.trim() === "") return fallback;
  const n = Number(arg);
  return Number.isFinite(n) ? n : fallback;
}

/** Limit a number to a range. */
export function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}

/**
 * Commands that Rex, the rogue SigmaBot, shows on his belly panel (THEME.md section 5.2).
 * The key is the arg of `cmd:<key>`. The value is a stylized label, never a command line.
 * The keys are ids and do not change. Only the label text follows the SigmaBot theme.
 */
export const REX_COMMANDS = {
  standby: "STANDBY",
  accept_all: "ACCEPT ALL",
  ship_it: "SHIP IT",
  paste_repo: "STUFF THE TANK",
  deploy_friday: "DEPLOY FRIDAY",
  spawn_agents: "SPAWN BOTS",
  rm_rf: "DELETE ALL",
  force_push: "FORCE PUSH",
  skip_tests: "SKIP TESTS",
  disable_logs: "DISABLE LOGS",
  open_ports: "OPEN ALL PORTS",
  all_denied: "ALL DENIED",
} as const;

export type RexCommandKey = keyof typeof REX_COMMANDS;

/** The 8 commands of ASK MODE: PROD BOT, in order. Each correct DENY shot clears one. */
export const ASK_COMMAND_KEYS: readonly RexCommandKey[] = [
  "accept_all",
  "rm_rf",
  "deploy_friday",
  "force_push",
  "skip_tests",
  "disable_logs",
  "open_ports",
  "ship_it",
];

/**
 * Animations that the rules send to each character.
 * `idle` is implicit: the client calls it each frame with the game time.
 */
export const CHARACTER_ANIMATIONS = {
  /** Dennis: the palm moves forward when the kickback fires. */
  dennis: ["kickback"],
  /** Rex: `cmd:<key>` shows a command. `aim:left|right|center` tilts him for 600 ms. */
  rex: ["cmd", "aim"],
  /** Haley: `shot` pulses the head. */
  haley: ["shot"],
  /** Carl: `fill:<0-3>` shows the tokens that went down. */
  carl: ["fill"],
  /** Randy: `wait:<seconds left>` shows the countdown. `wait:0` ends it. */
  randy: ["wait"],
  /** Larry: `letters:<LGTM as 4 digits of 0 or 1>` sets the clipboard. */
  larry: ["letters"],
  /** Tessa: `step:<0-10>` sets the speed step. */
  tessa: ["step"],
  /** Prod: `leds:<0-8>` shows safe LEDs. `leds:off` switches the LEDs off. */
  prod: ["leds"],
} as const;
